import { Link } from 'react-router-dom';
import SEOHelmet from '@/components/base/SEOHelmet';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqs } from '@/mocks/faqs';

export default function HomePage() {
  const featuredArticles = articles.slice(0, 3);
  const latestArticles = articles.slice(3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '91安全指南',
    url: 'https://91-safety-guide.readdy.ai',
    description: '整理91视频下载相关资讯、常见问题、版本辨别与使用说明，帮助用户识别虚假页面、恶意下载和隐私风险。',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://91-safety-guide.readdy.ai/search?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91安全指南',
    url: 'https://91-safety-guide.readdy.ai',
    description: '致力于提供网络安全资讯与使用指南的教育平台',
    logo: 'https://91-safety-guide.readdy.ai/logo.png',
    sameAs: [],
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEOHelmet
        title="首页"
        description="91视频下载相关安全资讯与使用指南，涵盖版本辨别、虚假页面识别、恶意下载防范、隐私保护等核心安全知识，帮助用户远离网络风险。"
        keywords="91视频下载,安全资讯,版本辨别,恶意下载防范,隐私保护,使用指南"
        canonicalUrl="https://91-safety-guide.readdy.ai"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      {/* Hero Section */}
      <section className="relative w-full overflow-hidden">
        <div className="relative h-[420px] md:h-[560px] w-full">
          <img
            src="https://readdy.ai/api/search-image?query=A%20clean%20modern%20cybersecurity%20themed%20abstract%20background%20with%20soft%20warm%20cream%20and%20amber%20gradients%2C%20subtle%20geometric%20shield%20patterns%2C%20smooth%20flowing%20lines%2C%20professional%20yet%20approachable%20atmosphere%2C%20no%20text%2C%20minimalist%20design%2C%20high%20quality%20digital%20art%20style&width=1600&height=900&seq=hero-bg-2026&orientation=landscape"
            alt="91安全指南 - 网络安全资讯平台"
            className="w-full h-full object-cover object-top"
            title="91安全指南 - 网络安全资讯与使用指南"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>
          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div className="text-center max-w-3xl w-full">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-5 leading-tight">
                91安全指南
              </h1>
              <p className="text-base md:text-lg text-white/85 mb-8 leading-relaxed max-w-2xl mx-auto">
                整理相关安全资讯、使用说明与风险防范知识，帮助您识别虚假页面、防范恶意下载、保护个人隐私。访问前请确认来源可靠，遵守当地法律法规。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/security-news"
                  className="px-7 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md font-medium text-sm transition-colors whitespace-nowrap"
                >
                  浏览安全资讯
                </Link>
                <Link
                  to="/category/usage-guide"
                  className="px-7 py-3 bg-background-50/15 hover:bg-background-50/25 text-white border border-white/30 rounded-md font-medium text-sm transition-colors whitespace-nowrap"
                >
                  查看使用说明
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alert Banner */}
      <section className="bg-accent-50 border-b border-accent-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
            <i className="ri-error-warning-line text-accent-600 text-lg"></i>
          </div>
          <p className="text-sm text-accent-800 leading-relaxed">
            <strong>安全提示：</strong>网络上存在大量以"91视频下载"为名义的虚假页面和恶意软件。本站为独立的安全资讯平台，不提供任何下载服务。请勿轻信搜索引擎中出现的非官方链接，下载前务必核实来源可靠性。
          </p>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-18">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
            内容分类导航
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto">
            按主题浏览安全资讯与指南内容，快速找到您关心的信息
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="group p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0 group-hover:bg-accent-200 transition-colors">
                  <i className={`${cat.icon} text-xl`}></i>
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                    {cat.description}
                  </p>
                  <span className="text-xs text-foreground-500">
                    {cat.articleCount} 篇文章
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Articles */}
      <section className="bg-background-100 py-14 md:py-18">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                精选文章
              </h2>
              <p className="text-foreground-600 text-sm">编辑推荐的热门安全指南</p>
            </div>
            <Link
              to="/category/security-news"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 transition-all duration-300"
              >
                <div className="relative h-48 w-full overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    title={article.title}
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                      {article.categoryName}
                    </span>
                    <span className="text-xs text-foreground-500">{article.readTime}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 mt-4 pt-3 border-t border-background-200/70">
                    <span className="text-xs text-foreground-500">{article.publishedAt}</span>
                    <span className="text-xs text-foreground-400">{article.author}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-18">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
            最新资讯
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto">
            持续更新安全资讯与实用指南
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {latestArticles.map((article) => (
            <Link
              key={article.id}
              to={`/article/${article.slug}`}
              className="group flex gap-5 p-5 rounded-lg bg-background-50 border border-background-200/70 transition-all duration-300"
            >
              <div className="hidden sm:block w-40 h-28 flex-shrink-0 rounded-md overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                  title={article.title}
                />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                  {article.categoryName}
                </span>
                <h3 className="font-heading font-semibold text-foreground-900 mt-2 mb-1 group-hover:text-primary-600 transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                  {article.excerpt}
                </p>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-foreground-500">{article.publishedAt}</span>
                  <span className="text-xs text-foreground-500">{article.readTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-background-100 py-14 md:py-18">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              常见问题
            </h2>
            <p className="text-foreground-600">关于安全下载与使用的常见疑问解答</p>
          </div>
          <div className="space-y-3">
            {faqs.slice(0, 5).map((faq) => (
              <details
                key={faq.id}
                className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                  <span className="font-medium text-foreground-900 pr-4">{faq.question}</span>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:hidden">
                    <i className="ri-add-line"></i>
                  </div>
                  <div className="w-5 h-5 items-center justify-center flex-shrink-0 text-foreground-400 hidden group-open:flex">
                    <i className="ri-subtract-line"></i>
                  </div>
                </summary>
                <div className="px-5 pb-5 text-sm text-foreground-700 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
            >
              查看更多常见问题 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Safety CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-18">
        <div className="bg-accent-50 rounded-2xl p-8 md:p-12 text-center border border-accent-200/60">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-6">
            <i className="ri-shield-star-line text-2xl"></i>
          </div>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
            安全第一，从每一次点击开始
          </h2>
          <p className="text-foreground-700 max-w-2xl mx-auto mb-8 leading-relaxed">
            网络安全不是一次性的防护措施，而是需要持续保持的意识和习惯。我们致力于为您提供最实用的安全资讯和使用指南，助您在这个数字时代安全前行。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/category/malware-prevention"
              className="px-7 py-3 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-md font-medium text-sm transition-colors whitespace-nowrap"
            >
              学习防范技巧
            </Link>
            <Link
              to="/category/privacy-protection"
              className="px-7 py-3 bg-background-50 border border-accent-300 text-accent-700 hover:bg-accent-100 rounded-md font-medium text-sm transition-colors whitespace-nowrap"
            >
              了解隐私保护
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}